/**
 * GithubRepoPicker — searchable single-select of the user's GitHub repos,
 * for a deploy-input field whose `deployInput.source === 'github-repo'`
 * (e.g. flaky-test-fixer's `repoUrl`).
 *
 * Data path: REUSES the existing `GET /github/repos` feed (the same one
 * ProjectSettings' multi-select uses — handler getGithubRepos). Response:
 *   { repos: [{ full_name, name, owner, private, default_branch, html_url }],
 *     needsReconnect?, needsSetup?, message? }
 * The user can have hundreds of repos, so this is a typeahead combobox:
 * a filterable text input + a clamped scroll list, NOT a giant <select>.
 *
 * VALUE shape: the repo's clone URL — `https://github.com/<full_name>` —
 * so it matches the `repoUrl` (z.string().url()) the template expects.
 * (We deliberately store the URL, not the slug, so the picker is a drop-
 * in for any `string().url()` repo field.)
 *
 * Receives the uniform picker props bag (see deployInputSources.js).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import {
  logoGithub,
  searchOutline,
  chevronDownOutline,
  checkmarkOutline,
  lockClosedOutline,
} from 'ionicons/icons';
import api from '../../../../services/api';

const urlForRepo = (fullName) => `https://github.com/${fullName}`;
// Tolerant inverse — pull the owner/name back out of a stored clone URL so
// we can show the matching repo as selected when the field is pre-seeded.
const fullNameFromUrl = (url) => {
  if (typeof url !== 'string') return '';
  const m = url.match(/github\.com[/:]([^/]+\/[^/.]+)(?:\.git)?\/?$/);
  return m ? m[1] : '';
};

export default function GithubRepoPicker({ value, onChange, disabled, error }) {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch the repo list once when the picker first mounts. Best-effort —
  // an empty/failed fetch leaves the field usable (the user can still
  // paste a URL via the search box, which we treat as a free-text value).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .get('/github/repos')
      .then((data) => {
        if (cancelled) return;
        setRepos(Array.isArray(data?.repos) ? data.repos : []);
        setNeedsReconnect(Boolean(data?.needsReconnect));
        setNeedsSetup(Boolean(data?.needsSetup));
      })
      .catch((err) => {
        if (cancelled) return;
        setRepos([]);
        setLoadError(err?.message || 'Failed to load GitHub repositories');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Close the dropdown on outside-click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedFullName = useMemo(() => fullNameFromUrl(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter((r) =>
      (r.full_name || '').toLowerCase().includes(q)
      || (r.name || '').toLowerCase().includes(q));
  }, [repos, query]);

  const choose = (repo) => {
    onChange(urlForRepo(repo.full_name));
    setOpen(false);
    setQuery('');
  };

  // Allow pasting a raw repo URL into the search box (covers repos the
  // listing API didn't return — forks, org repos outside the install).
  // When the query looks like a github URL and the user blurs / Enters,
  // commit it as the value verbatim.
  const commitFreeText = () => {
    const q = query.trim();
    if (q && /github\.com[/:][^/]+\/[^/]+/.test(q)) {
      onChange(q);
      setOpen(false);
      setQuery('');
    }
  };

  const displayLabel = selectedFullName || (value ? value : '');

  return (
    <div className={`deploy-input-ghrepo ${error ? 'has-error' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="deploy-input-ghrepo-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={Boolean(error)}
        onClick={() => {
          setOpen((v) => !v);
          // Focus the filter input on next paint when opening.
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <IonIcon icon={logoGithub} aria-hidden="true" />
        <span className={`deploy-input-ghrepo-value ${displayLabel ? '' : 'is-placeholder'}`}>
          {displayLabel || (loading ? 'Loading repositories…' : 'Select a repository…')}
        </span>
        <IonIcon icon={chevronDownOutline} aria-hidden="true" className="deploy-input-ghrepo-chevron" />
      </button>

      {open && (
        <div className="deploy-input-ghrepo-menu" role="listbox">
          <div className="deploy-input-ghrepo-search">
            <IonIcon icon={searchOutline} aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              className="deploy-input-ghrepo-search-input"
              placeholder="Search repositories or paste a URL…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filtered.length === 1) choose(filtered[0]);
                  else commitFreeText();
                }
              }}
              onBlur={commitFreeText}
              autoComplete="off"
              spellCheck="false"
            />
          </div>

          <ul className="deploy-input-ghrepo-list">
            {loading && (
              <li className="deploy-input-ghrepo-empty">Loading…</li>
            )}
            {!loading && needsSetup && (
              <li className="deploy-input-ghrepo-empty">
                GitHub isn’t connected. Connect it in Integrations to list your repos
                (or paste a repo URL above).
              </li>
            )}
            {!loading && !needsSetup && filtered.length === 0 && (
              <li className="deploy-input-ghrepo-empty">
                {repos.length === 0
                  ? (loadError || 'No repositories found. Paste a repo URL above.')
                  : 'No matches. Paste a repo URL above.'}
              </li>
            )}
            {!loading && filtered.map((r) => {
              const isSel = r.full_name === selectedFullName;
              return (
                <li key={r.full_name}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`deploy-input-ghrepo-option ${isSel ? 'is-selected' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                  >
                    <span className="deploy-input-ghrepo-option-name">{r.full_name}</span>
                    {r.private && (
                      <IonIcon
                        icon={lockClosedOutline}
                        aria-label="private"
                        className="deploy-input-ghrepo-option-lock"
                      />
                    )}
                    {isSel && (
                      <IonIcon icon={checkmarkOutline} aria-hidden="true" className="deploy-input-ghrepo-option-check" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {needsReconnect && (
            <p className="deploy-input-ghrepo-note">
              Only public repos are visible — reconnect GitHub in Integrations for private repos.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
